import React, { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { ContentContext } from "../contexts/ContentContext";

const SITE_ORIGIN =
    typeof window !== "undefined" ? window.location.origin : "https://northstaragro.com";

const DEFAULT_OG_IMAGE = "/images/og-default.jpg"; // optional asset; OK if missing in dev

/**
 * Per-page Helmet wrapper. Sets title, description, canonical,
 * hreflang alternates and OpenGraph + Twitter cards.
 *
 *   <Seo title="About" description="..." path="/about" image="/images/about-og.jpg" />
 */
export default function Seo({
    title,
    description,
    path = "/",
    image = DEFAULT_OG_IMAGE,
    type = "website",
    jsonLd,
}) {
    const { data, language } = useContext(ContentContext);
    const fullTitle = title
        ? `${title} · ${data.brand.name}`
        : data.meta?.siteTitle || data.brand.name;
    const desc =
        description || data.meta?.siteDescription || data.brand.tagline || "";
    const url = `${SITE_ORIGIN}${path === "/" ? "" : path}`;
    const ogImage = image && image.startsWith("http") ? image : `${SITE_ORIGIN}${image}`;

    return (
        <Helmet htmlAttributes={{ lang: language }}>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            <link rel="canonical" href={url} />

            {/* hreflang alternates — same path served per language via the
                in-app language switcher; search engines see one URL */}
            <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}${path}`} />
            <link rel="alternate" hrefLang="ru" href={`${SITE_ORIGIN}${path}`} />
            <link rel="alternate" hrefLang="ar" href={`${SITE_ORIGIN}${path}`} />
            <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${path}`} />

            {/* OpenGraph */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content={language === "ru" ? "ru_RU" : language === "ar" ? "ar_SA" : "en_US"} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            <meta name="twitter:image" content={ogImage} />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}

/**
 * Returns the Organization JSON-LD object — call once on the home page.
 */
export function buildOrganizationJsonLd(data) {
    return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: data.brand.name,
        legalName: data.legal?.registeredName || data.brand.name,
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/logo512.png`,
        foundingDate: "2005",
        founders: (data.founders?.items || []).map((f) => ({
            "@type": "Person",
            name: f.name,
            jobTitle: f.title,
        })),
        address: {
            "@type": "PostalAddress",
            streetAddress: data.legal?.address || data.contact.address,
            addressLocality: "Bishkek",
            postalCode: "720040",
            addressCountry: "KG",
        },
        contactPoint: [
            {
                "@type": "ContactPoint",
                contactType: "sales",
                telephone: data.contact.phoneE164 || data.contact.phone,
                email: data.contact.email,
                areaServed: ["KG", "SA", "RU", "MY", "AE"],
                availableLanguage: ["English", "Russian", "Arabic"],
            },
        ],
        sameAs: data.social
            ? Object.values(data.social).filter(Boolean)
            : [],
    };
}

export function buildLocalBusinessJsonLd(data) {
    return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: data.brand.name,
        image: `${SITE_ORIGIN}/logo512.png`,
        telephone: data.contact.phoneE164 || data.contact.phone,
        email: data.contact.email,
        address: {
            "@type": "PostalAddress",
            streetAddress: data.legal?.address || data.contact.address,
            addressLocality: "Bishkek",
            postalCode: "720040",
            addressCountry: "KG",
        },
        openingHours: "Su-Th 09:00-18:00",
    };
}

export function buildProductJsonLd(item) {
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: item.name,
        description: item.tasteProfile,
        image: `${SITE_ORIGIN}${item.image}`,
        brand: { "@type": "Brand", name: "North Star LLC" },
        sku: item.id,
    };
}
